/* eslint-disable max-classes-per-file */
type AvailableTypes = string | number | symbol;

type StoredValue<TKey extends AvailableTypes, TValue extends AvailableTypes> = { [item in TKey]: TValue };

export class TwoWayObject<TKey extends AvailableTypes, TValue extends AvailableTypes> {
    // eslint-disable-next-line no-useless-constructor
    constructor(
        private keysFirst: StoredValue<TKey, TValue>,
        private valuesFirst: StoredValue<TValue, TKey> // eslint-disable-next-line no-empty-function
    ) {}

    getValue(
        key: TKey,
        handleMissingKey: (key: TKey) => never | null | undefined = (key: TKey) => {
            throw new Error(`Value ${String(key)} does not exist`);
        }
    ) {
        if (!Object.prototype.hasOwnProperty.call(this.keysFirst, key)) {
            return handleMissingKey(key);
        }
        return this.keysFirst[key];
    }

    getKey(value: TValue) {
        return this.valuesFirst[value];
    }

    hasKey(key: TKey) {
        return Object.prototype.hasOwnProperty.call(this.keysFirst, key);
    }

    hasValue(value: TValue) {
        return Object.prototype.hasOwnProperty.call(this.valuesFirst, value);
    }

    create(key: TKey, value: TValue) {
        const newKeys = {
            ...this.keysFirst,
            [key]: value,
        };

        const newValues = {
            ...this.valuesFirst,
            [value]: key,
        };

        return new TwoWayObject(newKeys, newValues);
    }

    createKey(
        key: TKey,
        handleKeyExisting: (key: TKey) => never | null | undefined = (key: TKey) => {
            throw new Error(`Key ${String(key)} already exists`);
        }
    ) {
        if (this.keysFirst[key]) {
            return handleKeyExisting(key);
        }
        const newKeys = {
            ...this.keysFirst,
            [key]: undefined,
        };

        return new TwoWayObject(newKeys, this.valuesFirst);
    }

    createMultiple(pairs: { key: TKey; value: TValue }[]) {
        pairs.forEach(({ key, value }) => {
            if (this.valuesFirst[value]) {
                delete this.valuesFirst[value];
            }
            if (this.keysFirst[key]) {
                delete this.keysFirst[key];
            }
        });

        const newKeys = {
            ...this.keysFirst,
            ...pairs.reduce(
                (cum, { key, value }) => ({
                    ...cum,
                    [key]: value,
                }),
                {} as StoredValue<TKey, TValue>
            ),
        };

        const newValues = {
            ...this.valuesFirst,
            ...pairs.reduce(
                (cum, { key, value }) => ({
                    ...cum,
                    [value]: key,
                }),
                {} as StoredValue<TValue, TKey>
            ),
        };
        return new TwoWayObject(newKeys, newValues);
    }

    createMultipleKeys(
        keys: TKey[],
        handleKeyExisting: (key: TKey) => never | null | undefined = (key: TKey) => {
            throw new Error(`Key ${String(key)} already exists`);
        }
    ) {
        let newKeys = this.keysFirst;
        if (
            keys.forEach((key) => {
                if (this.keysFirst[key]) {
                    return handleKeyExisting(key);
                }
                return true;
            }) == null
        ) {
            return null;
        }
        keys.forEach((key) => {
            newKeys = {
                ...newKeys,
                [key]: undefined,
            };
        });
        return new TwoWayObject<TKey, TValue>(newKeys, this.valuesFirst);
    }

    getKeysFirst() {
        return this.keysFirst;
    }

    getValueFirst() {
        return this.valuesFirst;
    }
}
